import pointer from '../../utils/json-pointer'
import type { SharedStateId } from '../../../../shared/helpers/useSharedState'
import {
  createReferenceKey,
  createSharedState,
} from '../../../../shared/helpers/useSharedState'
import type { SharedAttachments } from '../../DataContext/Provider'
import type { Path } from '../../types'
import type {
  UseDataReturnGetValue,
  UseDataReturnFilterData,
  UseDataReturnVisibleData,
} from './useData'

type SetDataReturn<Data> = {
  data: Data
  getValue: UseDataReturnGetValue<Data>
  filterData: UseDataReturnFilterData<Data>
  reduceToVisibleFields: UseDataReturnVisibleData<Data>
}

export default function getData<Data>(
  id: SharedStateId
): SetDataReturn<Data> {
  const sharedState = createSharedState(id)
  const sharedAttachments = createSharedState<SharedAttachments<Data>>(
    createReferenceKey(id, 'attachments')
  )

  const data = sharedState.get() as Data

  const filterData: SetDataReturn<Data>['filterData'] = (filter) =>
    sharedAttachments.data?.filterDataHandler?.(data, filter)

  const reduceToVisibleFields: SetDataReturn<Data>['reduceToVisibleFields'] =
    (data, options) =>
      sharedAttachments.data?.visibleDataHandler?.(data, options)

  // The runtime resolver takes a plain `Path`; cast it to the public generic
  // signature so callers get the value type resolved from the literal path
  // (`getValue` maps `P` to `PathType<Data, P>`).
  const getValue = ((path: Path) => {
    if (pointer.has(data, path)) {
      return pointer.get(data, path)
    }

    return undefined
  }) as unknown as UseDataReturnGetValue<Data>

  return {
    data,
    getValue,
    filterData,
    reduceToVisibleFields,
  }
}
